import { createDeferred } from '../../utils/promise/defer';
import { extractCancelAndProgressConfig, isAborted } from '../../utils/http';
import { UploadStatus } from '../../constants/upload-status';
import { generateRandomGuid4 } from '../../utils/utils';
import { HttpClient } from '@wix/http-client';
import * as tus from 'tus-js-client';
import { getTusErrorReason } from '../../utils/tusProtocol/utils';
function promisify(value) {
  if (value.then) {
    return value;
  }
  return Promise.resolve(value);
}
function updateResumeBiEvents(fileToUpload, isAutomaticResume) {
  if (isAutomaticResume) {
    fileToUpload.bi.uploadType = 'auto_resumed';
    fileToUpload.bi.autoResumeCount += 1;
  } else {
    fileToUpload.bi.manualResumeCount += 1;
    fileToUpload.bi.uploadType = 'user_resumed';
  }
}
const canAbort = fileToUpload => fileToUpload.status === UploadStatus.Waiting || fileToUpload.status === UploadStatus.Uploading;
function abortEarly(fileToUpload) {
  fileToUpload.deferred.promise.catch(function (reason) {
    fileToUpload.failedOnStage = fileToUpload.status;
    fileToUpload.status = UploadStatus.Failed;
    fileToUpload.error = reason;
  });
  const httpClient = new HttpClient();
  const source = httpClient.CancelToken.source();
  fileToUpload.deferred.reject(source.cancel('aborted early by user'));
}
function initUpload(fileToUpload, data) {
  fileToUpload.status = UploadStatus.Uploading;
  let uploadFnPromise;
  if (fileToUpload.isResumable()) {
    uploadFnPromise = fileToUpload.cancelAndProgressConfig.tusOptions.resume();
  } else {
    const uploadFnValue = data.uploadFn(fileToUpload.source, fileToUpload.options, fileToUpload.cancelAndProgressConfig, fileToUpload.isTus);
    uploadFnPromise = promisify(uploadFnValue);
  }
  fileToUpload.uploadPromise = uploadFnPromise.then(function (res) {
    fileToUpload.deferred.resolve(res);
  }).catch(function (reason) {
    fileToUpload.failedOnStage = fileToUpload.status;
    fileToUpload.status = UploadStatus.Failed;
    fileToUpload.error = reason;
    fileToUpload.bi.TusErrorReason = fileToUpload.isTus ? getTusErrorReason(reason) : '';
    fileToUpload.deferred.reject(reason);
  });
}
export function toFileToUpload(data) {
  return {
    id: generateRandomGuid4(),
    isTus: data.options && data.options.isTus && tus.isSupported || false,
    bi: {
      manualResumeCount: 0,
      autoResumeCount: 0,
      uploadType: 'upload',
      TusErrorReason: ''
    },
    adapter: data.adapter || {},
    source: data.source,
    options: data.options,
    status: UploadStatus.Waiting,
    canReportStatusChange: true,
    cancelAndProgressConfig: extractCancelAndProgressConfig(),
    bytesLoaded: 0,
    currentChunkBytesLoaded: 0,
    deferred: createDeferred(),
    setCanReportStatusChange(canReportStatusChange) {
      this.canReportStatusChange = canReportStatusChange;
    },
    isAborted() {
      return isAborted(this.error);
    },
    error: {},
    upload() {
      if (!this.isAborted()) {
        initUpload(this, data);
      }
      return this.uploadPromise;
    },
    abort() {
      if (!canAbort(this)) {
        return;
      }
      if (this.status === UploadStatus.Waiting) {
        abortEarly(this);
        return;
      }
      if (this.isTus && this.cancelAndProgressConfig.tusOptions.abort) {
        this.cancelAndProgressConfig.tusOptions.abort();
        return;
      }
      this.cancelAndProgressConfig.abort();
    },
    canRetry() {
      if (this.isAborted()) {
        return true;
      }
      const codesToRetry = [-201, 401, 403, 408];
      return this.status === UploadStatus.Failed && codesToRetry.indexOf((this.error || {}).code) !== -1;
    },
    isResumable() {
      return this.isTus && this.cancelAndProgressConfig.tusOptions.isResumable && this.cancelAndProgressConfig.tusOptions.isResumable();
    },
    isInAutoResume() {
      return this.isResumable() && this.cancelAndProgressConfig.tusOptions.shouldTryToResume && this.cancelAndProgressConfig.tusOptions.shouldTryToResume();
    },
    canResume() {
      return this.isAborted() && this.isResumable();
    },
    signUpForResume(isAutomaticResume) {
      if (isAutomaticResume === void 0) {
        isAutomaticResume = false;
      }
      updateResumeBiEvents(this, isAutomaticResume);
      this.status = UploadStatus.Waiting;
      this.error = {};
      this.deferred = createDeferred();
    },
    signUpForRetry() {
      this.bi.uploadType = 'upload';
      this.status = UploadStatus.Waiting;
      this.error = {};
      this.cancelAndProgressConfig = extractCancelAndProgressConfig();
      this.deferred = createDeferred();
    },
    canAbort() {
      return canAbort(this);
    },
    getFileSize() {
      return this.source.file ? this.source.file.size : 1;
    }
  };
}